import {
  Doctype,
  DoctypeListResponse,
  DoctypeResponse,
} from "./types";
import { Settings } from "./settings";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function buildHeaders(settings: Settings): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Dragon-Law-API-Version": settings.apiVersion,
  };
  if (settings.dragonball) headers["X-Dragon-Law-Dragonball"] = settings.dragonball;
  if (settings.username) headers["X-Dragon-Law-Username"] = settings.username;
  return headers;
}

async function request<T>(
  settings: Settings,
  path: string,
  init?: RequestInit
): Promise<T> {
  const url = settings.apiUrl.replace(/\/$/, "") + path;
  const response = await fetch(url, {
    ...init,
    credentials: "include",
    headers: { ...buildHeaders(settings), ...(init?.headers || {}) },
  });

  const text = await response.text();
  let body: unknown = text;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // leave as text
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body,
      `Request failed: ${response.status} ${response.statusText}`
    );
  }
  return body as T;
}

// Exchanges a user-issued API key (sk_* / pk_*) for a Dragonball token.
// Backend: GET /api/auth/:key returns { dragonball, username, userId, orgId }.
// This lets us reuse the existing header pipeline once we have a Dragonball.
export async function exchangeApiKey(
  settings: Settings,
  apiKey: string
): Promise<{ dragonball: string; username: string }> {
  const url = settings.apiUrl.replace(/\/$/, "") + `/api/auth/${encodeURIComponent(apiKey)}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "X-Dragon-Law-API-Version": settings.apiVersion,
      "X-Api-Key": apiKey,
    },
  });

  const text = await response.text();
  type ExchangeBody = {
    dragonball?: string;
    username?: string;
    message?: string;
    results?: { dragonball?: string; username?: string };
  };
  let body: ExchangeBody = {};
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // leave as empty
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body,
      body.message || `API key exchange failed (${response.status})`
    );
  }

  const dragonball =
    body.results?.dragonball ||
    body.dragonball ||
    response.headers.get("x-dragon-law-dragonball") ||
    "";
  const username =
    body.results?.username ||
    body.username ||
    response.headers.get("x-dragon-law-username") ||
    "";

  if (!dragonball) {
    throw new ApiError(
      response.status,
      body,
      "API key exchange succeeded but no Dragonball token was returned."
    );
  }

  return { dragonball, username };
}

// Mirrors zegal-apps' session.login(): POST /auth/login. The dragonball
// token and username come back in response headers, not the body.
// Same-origin proxy means CORS expose rules don't hide them from JS.
export async function login(
  settings: Settings,
  email: string,
  password: string
): Promise<{ dragonball: string; username: string }> {
  const url = settings.apiUrl.replace(/\/$/, "") + "/auth/login";
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Dragon-Law-API-Version": settings.apiVersion,
    },
    body: JSON.stringify({ email, password }),
  });

  const text = await response.text();
  let body: unknown = text;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // leave as raw text
    }
  }

  if (!response.ok) {
    const message =
      (body && typeof body === "object" && "message" in body && String(body.message)) ||
      `Login failed (${response.status})`;
    throw new ApiError(response.status, body, message);
  }

  const dragonball = response.headers.get("x-dragon-law-dragonball") || "";
  const username = response.headers.get("x-dragon-law-username") || email;

  if (!dragonball) {
    throw new ApiError(
      response.status,
      body,
      "Login succeeded but no X-Dragon-Law-Dragonball in response headers."
    );
  }

  return { dragonball, username };
}

export async function listDoctypes(
  settings: Settings,
  options: { search?: string; page?: number; limit?: number } = {}
): Promise<DoctypeListResponse> {
  const params = new URLSearchParams({ v: "3", limit: String(options.limit ?? 20) });
  if (options.search) params.set("search", options.search);
  if (options.page) params.set("page", String(options.page));
  return request<DoctypeListResponse>(settings, `/doctypes?${params.toString()}`);
}

export async function getDoctype(settings: Settings, id: string): Promise<Doctype> {
  const response = await request<DoctypeResponse>(settings, `/doctypes/v3/${id}`);
  return response.results;
}

// Updates the DOCX content of an existing doctype (template).
// Mirrors zegal-apps' doctype.saveDocxFile():
//   PUT /doctypes/v3/{id}/docxFile  body { docxFile: <base64> }
export async function updateDoctypeDocxFile(
  settings: Settings,
  id: string,
  docxBase64: string
): Promise<DoctypeResponse> {
  return request<DoctypeResponse>(settings, `/doctypes/v3/${id}/docxFile`, {
    method: "PUT",
    body: JSON.stringify({ docxFile: docxBase64 }),
  });
}
